import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

// Types
type AlertType = 'success' | 'error' | 'warning' | 'info'
type AlertPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right' | 'center'

interface Alert {
  id: string
  type: AlertType
  message: string
  timeout?: number
  onClose?: () => void
}

interface AlertConfig {
  position: AlertPosition
  maxAlerts: number
}

interface AlertContextType {
  showAlert: (alert: Omit<Alert, 'id'>) => void
  hideAlert: (id: string) => void
  alerts: Alert[]
  config: AlertConfig
  setConfig: (config: Partial<AlertConfig>) => void
}

// Context
const AlertContext = createContext<AlertContextType | undefined>(undefined)

// Icons for each alert type
const AlertIcons = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ'
}

// Individual Toast Component
const Toast: React.FC<{
  alert: Alert
  onClose: (id: string) => void
  style?: React.CSSProperties
}> = ({ alert, onClose, style }) => {
  const [progress, setProgress] = useState(100)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    // Entrance animation
    setTimeout(() => setIsVisible(true), 10)

    if (alert.timeout) {
      const duration = alert.timeout
      const interval = 50
      const decrement = (interval / duration) * 100

      const timer = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev - decrement
          if (newProgress <= 0) {
            clearInterval(timer)
            handleClose()
            return 0
          }
          return newProgress
        })
      }, interval)

      return () => clearInterval(timer)
    }
  }, [alert.timeout])

  const handleClose = useCallback(() => {
    setIsVisible(false)
    setTimeout(() => {
      onClose(alert.id)
      alert.onClose?.()
    }, 300)
  }, [alert.id, alert.onClose, onClose])

  return (
    <div 
      className={`toast toast-${alert.type} ${isVisible ? 'toast-visible' : ''}`}
      role="alert"
      aria-live="polite"
      style={style}
    >
      <div className="toast-content">
        <span className="toast-icon" aria-hidden="true">
          {AlertIcons[alert.type]}
        </span>
        <span className="toast-message">{alert.message}</span>
        <button 
          className="toast-close" 
          onClick={handleClose}
          aria-label="Close alert"
          type="button"
        >
          ✕
        </button>
      </div>
      {alert.timeout && (
        <div className="toast-progress-container">
          <div 
            className="toast-progress" 
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  )
}

// Toast Container Component
const ToastContainer: React.FC = () => {
  const { alerts, hideAlert, config } = useAlert()

  useEffect(() => {
    // Ensure alert-root exists
    let alertRoot = document.getElementById('alert-root')
    if (!alertRoot) {
      alertRoot = document.createElement('div')
      alertRoot.id = 'alert-root'
      document.body.appendChild(alertRoot)
    }
  }, [])

  const getPositionStyles = (position: AlertPosition) => {
    const baseStyles = {
      position: 'fixed' as const,
      zIndex: 9999,
      display: 'flex',
      pointerEvents: 'none' as const,
      padding: '1rem',
      boxSizing: 'border-box' as const
    }

    switch (position) {
      case 'top-left':
        return { ...baseStyles, top: 0, left: 0, flexDirection: 'column' as const }
      case 'top-center':
        return { ...baseStyles, top: 0, left: '50%', transform: 'translateX(-50%)', flexDirection: 'column' as const }
      case 'top-right':
        return { ...baseStyles, top: 0, right: 0, flexDirection: 'column' as const }
      case 'bottom-left':
        return { ...baseStyles, bottom: 0, left: 0, flexDirection: 'column-reverse' as const }
      case 'bottom-center':
        return { ...baseStyles, bottom: 0, left: '50%', transform: 'translateX(-50%)', flexDirection: 'column-reverse' as const }
      case 'bottom-right':
        return { ...baseStyles, bottom: 0, right: 0, flexDirection: 'column-reverse' as const }
      case 'center':
        return { 
          ...baseStyles, 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          flexDirection: 'column' as const,
          alignItems: 'center'
        }
      default:
        return { ...baseStyles, top: 0, right: 0, flexDirection: 'column' as const }
    }
  }

  const alertRoot = document.getElementById('alert-root')
  if (!alertRoot) return null

  // Limit alerts based on maxAlerts config
  const visibleAlerts = alerts.slice(0, config.maxAlerts)

  return createPortal(
    <div className="toast-container" style={getPositionStyles(config.position)}>
      {visibleAlerts.map((alert, index) => (
        <Toast
          key={alert.id}
          alert={alert}
          onClose={hideAlert}
          style={{ 
            marginBottom: config.position.startsWith('bottom') ? (index < visibleAlerts.length - 1 ? '0.75rem' : '0') : '0',
            marginTop: config.position.startsWith('top') || config.position === 'center' ? (index < visibleAlerts.length - 1 ? '0.75rem' : '0') : '0'
          }}
        />
      ))}
      <style>{`
        .toast-container {
          max-width: 100vw;
          max-height: 100vh;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
        }

        .toast {
          background: var(--pico-contrast);
          color: var(--pico-contrast-inverse);
          border: 2px solid var(--pico-contrast-inverse);
          border-radius: var(--pico-border-radius);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          min-width: 320px;
          max-width: 400px;
          pointer-events: auto;
          transform: translateX(${config.position.includes('left') ? '-100%' : config.position.includes('right') ? '100%' : '0'});
          opacity: 0;
          transition: all 0.3s ease-in-out;
          overflow: hidden;
          position: relative;
        }

        .toast-visible {
          transform: translateX(0) translateY(0);
          opacity: 1;
        }

        .toast-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
        }

        .toast-icon {
          font-size: 1.25rem;
          font-weight: bold;
          flex-shrink: 0;
        }

        .toast-message {
          flex: 1;
          font-size: 0.875rem;
          line-height: 1.4;
          word-wrap: break-word;
        }

        .toast-close {
          background: transparent;
          border: none;
          color: var(--pico-contrast-inverse);
          cursor: pointer;
          font-size: 1.125rem;
          padding: 0.25rem;
          border-radius: var(--pico-border-radius);
          transition: background-color 0.2s ease;
          flex-shrink: 0;
          opacity: 0.7;
        }

        .toast-close:hover {
          background-color: rgba(255, 255, 255, 0.1);
          opacity: 1;
        }

        .toast-progress-container {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background-color: rgba(255, 255, 255, 0.2);
        }

        .toast-progress {
          height: 100%;
          transition: width 0.05s linear;
          border-radius: 0 0 var(--pico-border-radius) var(--pico-border-radius);
        }

        .toast-success .toast-icon {
          color: #22c55e;
        }

        .toast-success .toast-progress {
          background-color: #22c55e;
        }

        .toast-error .toast-icon {
          color: #ef4444;
        }

        .toast-error .toast-progress {
          background-color: #ef4444;
        }

        .toast-warning .toast-icon {
          color: #f59e0b;
        }

        .toast-warning .toast-progress {
          background-color: #f59e0b;
        }

        .toast-info .toast-icon {
          color: #3b82f6;
        }

        .toast-info .toast-progress {
          background-color: #3b82f6;
        }

        @media (max-width: 768px) {
          .toast-container {;
            padding: 0.5rem !important;
          }

          .toast {
            min-width: 280px;
            max-width: calc(100vw - 1rem);
          }
        }
      `}</style>
    </div>,
    alertRoot
  )
}

// Alert Provider Component
const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [config, setConfigState] = useState<AlertConfig>({
    position: 'top-right',
    maxAlerts: 5
  })

  const showAlert = useCallback((alertData: Omit<Alert, 'id'>) => {
    const id = `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newAlert: Alert = {
      ...alertData,
      id,
      //timeout: alertData.timeout ?? 5000 // Default 5 seconds
      timeout: 1000 * 120 // Default 5 seconds
    }

    setAlerts(prev => {
      const updated = [newAlert, ...prev]
      // Remove excess alerts beyond maxAlerts
      return updated.slice(0, config.maxAlerts)
    })
  }, [config.maxAlerts])

  const hideAlert = useCallback((id: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id))
  }, [])

  const setConfig = useCallback((newConfig: Partial<AlertConfig>) => {
    setConfigState(prev => {
      const updated = { ...prev, ...newConfig }
      
      // If maxAlerts changed and we have more alerts, remove excess
      if (newConfig.maxAlerts !== undefined && alerts.length > newConfig.maxAlerts) {
        setAlerts(current => current.slice(0, newConfig.maxAlerts))
      }
      
      return updated
    })
  }, [alerts.length])

  // Register the showAlert function with the singleton
  useEffect(() => {
    alertManager.register(showAlert, setConfig)
    return () => alertManager.unregister()
  }, [showAlert, setConfig])

  const value: AlertContextType = {
    showAlert,
    hideAlert,
    alerts,
    config,
    setConfig
  }

  return (
    <AlertContext.Provider value={value}>
      {children}
      <ToastContainer />
    </AlertContext.Provider>
  )
}

// Singleton Alert Manager for non-React services
class AlertManager {
  private static instance: AlertManager
  private showAlertFn: ((alert: Omit<Alert, 'id'>) => void) | null = null
  private setConfigFn: ((config: Partial<AlertConfig>) => void) | null = null

  private constructor() {}

  static getInstance(): AlertManager {
    if (!AlertManager.instance) {
      AlertManager.instance = new AlertManager()
    }
    return AlertManager.instance
  }

  // Called by AlertProvider to register functions
  register(
    showAlert: (alert: Omit<Alert, 'id'>) => void,
    setConfig: (config: Partial<AlertConfig>) => void
  ) {
    this.showAlertFn = showAlert
    this.setConfigFn = setConfig
  }

  // Called by AlertProvider when unmounting
  unregister() {
    this.showAlertFn = null
    this.setConfigFn = null
  }

  // Public method for services to show alerts
  showAlert(alert: Omit<Alert, 'id'>) {
    if (this.showAlertFn) {
      this.showAlertFn(alert)
    } else {
      console.warn('AlertManager: No alert provider registered. Make sure AlertProvider is mounted.')
    }
  }

  // Configure alert system
  configure(config: Partial<AlertConfig>) {
    if (this.setConfigFn) {
      this.setConfigFn(config)
    } else {
      console.warn('AlertManager: No alert provider registered. Make sure AlertProvider is mounted.')
    }
  }

  // Convenience methods for different alert types
  success(message: string, options?: Partial<Omit<Alert, 'id' | 'type' | 'message'>>) {
    this.showAlert({ type: 'success', message, ...options })
  }

  error(message: string, options?: Partial<Omit<Alert, 'id' | 'type' | 'message'>>) {
    this.showAlert({ type: 'error', message, ...options })
  }

  warning(message: string, options?: Partial<Omit<Alert, 'id' | 'type' | 'message'>>) {
    this.showAlert({ type: 'warning', message, ...options })
  }

  info(message: string, options?: Partial<Omit<Alert, 'id' | 'type' | 'message'>>) {
    this.showAlert({ type: 'info', message, ...options })
  }
}

// Export singleton instance
export const alertManager = AlertManager.getInstance()

// Custom Hook
const useAlert = () => {
  const context = useContext(AlertContext)
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}

// Example API Service using the alert system
class ApiService {
  private baseURL = 'https://jsonplaceholder.typicode.com'

  // Simulated axios-like HTTP client
  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    try {
      const response = await fetch(`${this.baseURL}${url}`, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      // Handle different types of errors
      if (error instanceof TypeError) {
        alertManager.error('Network error: Please check your internet connection')
      } else if (error instanceof Error) {
        if (error.message.includes('404')) {
          alertManager.error('Resource not found')
        } else if (error.message.includes('401')) {
          alertManager.error('Authentication required')
        } else if (error.message.includes('403')) {
          alertManager.error('Access denied')
        } else if (error.message.includes('500')) {
          alertManager.error('Server error: Please try again later')
        } else {
          alertManager.error(`API Error: ${error.message}`)
        }
      } else {
        alertManager.error('An unexpected error occurred')
      }
      throw error
    }
  }

  // Example API methods
  async getUsers() {
    try {
      alertManager.info('Loading users...', { timeout: 1000 })
      const users = await this.request<any[]>('/users')
      alertManager.success(`Successfully loaded ${users.length} users`)
      return users
    } catch (error) {
      // Error already handled in request method
      throw error
    }
  }

  async getUserById(id: number) {
    try {
      const user = await this.request<any>(`/users/${id}`)
      alertManager.success('User loaded successfully')
      return user
    } catch (error) {
      throw error
    }
  }

  async createUser(userData: any) {
    try {
      alertManager.info('Creating user...', { timeout: 1000 })
      const user = await this.request<any>('/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      })
      alertManager.success('User created successfully!')
      return user
    } catch (error) {
      throw error
    }
  }

  async updateUser(id: number, userData: any) {
    try {
      alertManager.info('Updating user...', { timeout: 1000 })
      const user = await this.request<any>(`/users/${id}`, {
        method: 'PUT',
        body: JSON.stringify(userData)
      })
      alertManager.success('User updated successfully!')
      return user
    } catch (error) {
      throw error
    }
  }

  async deleteUser(id: number) {
    try {
      alertManager.warning('Deleting user...', { timeout: 1000 })
      await this.request(`/users/${id}`, { method: 'DELETE' })
      alertManager.success('User deleted successfully!')
    } catch (error) {
      throw error
    }
  }

  // Simulate network error
  async simulateNetworkError() {
    try {
      await this.request('/invalid-endpoint-that-will-fail')
    } catch (error) {
      // Error handled automatically
    }
  }

  // Simulate validation error
  async simulateValidationError() {
    alertManager.error('Validation failed: Email is required', { timeout: 6000 })
  }
}

// Export singleton instance
export const apiService = new ApiService()


// Demo Component
export const Demo: React.FC = () => {
  const { showAlert, config, setConfig } = useAlert()

  const handleSuccess = () => {
    showAlert({
      type: 'success',
      message: 'Operation completed successfully!',
      timeout: 4000
    })
  }

  const handleError = () => {
    showAlert({
      type: 'error',
      message: 'Something went wrong. Please try again.',
      timeout: 6000
    })
  }

  const handleWarning = () => {
    showAlert({
      type: 'warning',
      message: 'This action cannot be undone. Are you sure?',
      timeout: 5000
    })
  }

  const handleInfo = () => {
    showAlert({
      type: 'info',
      message: 'Here is some helpful information for you.',
      timeout: 3000
    })
  }

  const handlePersistent = () => {
    showAlert({
      type: 'info',
      message: 'This alert will stay until you close it manually.',
      onClose: () => console.log('Persistent alert closed')
    })
  }

  const handleQueue = () => {
    showAlert({ type: 'info', message: 'First alert in queue', timeout: 2000 })
    setTimeout(() => {
      showAlert({ type: 'success', message: 'Second alert in queue', timeout: 2000 })
    }, 500)
    setTimeout(() => {
      showAlert({ type: 'warning', message: 'Third alert in queue', timeout: 2000 })
    }, 1000)
  }

  const handleDirectAccess = () => {
    // Demonstrate direct access from anywhere in the app
    alertManager.success('This alert was triggered using the singleton!')
    setTimeout(() => {
      alertManager.warning('Multiple alerts from singleton', { timeout: 3000 })
    }, 1000)
  }

  const handleSpamAlerts = () => {
    for (let i = 1; i <= 10; i++) {
      setTimeout(() => {
        alertManager.info(`Spam alert #${i}`, { timeout: 2000 })
      }, i * 200)
    }
  }

  const handlePositionChange = (position: AlertPosition) => {
    setConfig({ position })
    alertManager.info(`Position changed to ${position}`)
  }

  const handleMaxAlertsChange = (maxAlerts: number) => {
    setConfig({ maxAlerts })
    alertManager.info(`Max alerts set to ${maxAlerts}`)
  }

  return (
    <main className="container">
      <h1>PicoCSS Toast Alert System</h1>
      
      <section>
        <h2>Configuration</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label>Position:</label>
            <select 
              value={config.position} 
              onChange={(e) => handlePositionChange(e.target.value as AlertPosition)}
            >
              <option value="top-left">Top Left</option>
              <option value="top-center">Top Center</option>
              <option value="top-right">Top Right</option>
              <option value="bottom-left">Bottom Left</option>
              <option value="bottom-center">Bottom Center</option>
              <option value="bottom-right">Bottom Right</option>
              <option value="center">Center</option>
            </select>
          </div>
          <div>
            <label>Max Alerts:</label>
            <select 
              value={config.maxAlerts} 
              onChange={(e) => handleMaxAlertsChange(Number(e.target.value))}
            >
              <option value={1}>1</option>
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
          </div>
        </div>
      </section>
      
      <section>
        <h2>Alert Types (via Hook)</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={handleSuccess}>Success Alert</button>
          <button onClick={handleError} className="secondary">Error Alert</button>
          <button onClick={handleWarning} className="contrast">Warning Alert</button>
          <button onClick={handleInfo} className="outline">Info Alert</button>
        </div>
      </section>

      <section>
        <h2>Special Features</h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={handlePersistent} className="outline secondary">
            Persistent Alert
          </button>
          <button onClick={handleQueue} className="outline contrast">
            Queue Multiple Alerts
          </button>
        </div>
      </section>

      <section>
        <h2>Features</h2>
        <ul>
          <li>✅ TypeScript support with no semicolons</li>
          <li>✅ Global alert context and provider</li>
          <li>✅ Auto-dismiss with configurable timeout</li>
          <li>✅ Progress bar showing remaining time</li>
          <li>✅ Portal rendering to #alert-root</li>
          <li>✅ Alert queue management</li>
          <li>✅ Icons for success, error, warning, info</li>
          <li>✅ Customizable alert text</li>
          <li>✅ Close button with custom handlers</li>
          <li>✅ Responsive design</li>
          <li>✅ Smooth animations</li>
          <li>✅ PicoCSS styling integration</li>
        </ul>
      </section>
    </main>
  )
}


// Demo Component with Service Integration
export const ServiceDemo: React.FC = () => {
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  const handleLoadUsers = async () => {
    setLoading(true)
    try {
      const userData = await apiService.getUsers()
      setUsers(userData.slice(0, 3)) // Show first 3 users
    } catch (error) {
      // Error already shown via alert
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async () => {
    try {
      await apiService.createUser({
        name: 'John Doe',
        email: 'john@example.com',
        username: 'johndoe'
      })
      // Refresh users list
      handleLoadUsers()
    } catch (error) {
      // Error already shown via alert
    }
  }

  const handleSimulateError = () => {
    apiService.simulateNetworkError()
  }

  const handleValidationError = () => {
    apiService.simulateValidationError()
  }

  return (
    <section>
      <h2>API Service Integration</h2>
      <p>This demonstrates how to use alerts from external services that aren't React components.</p>
      
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button onClick={handleLoadUsers} disabled={loading}>
          {loading ? 'Loading...' : 'Load Users'}
        </button>
        <button onClick={handleCreateUser} className="secondary">
          Create User
        </button>
        <button onClick={handleSimulateError} className="outline contrast">
          Simulate Network Error
        </button>
        <button onClick={handleValidationError} className="outline secondary">
          Simulate Validation Error
        </button>
      </div>

      {users.length > 0 && (
        <div>
          <h3>Loaded Users:</h3>
          <ul>
            {users.map(user => (
              <li key={user.id}>{user.name} ({user.email})</li>
            ))}
          </ul>
        </div>
      )}

      <details>
        <summary>Service Usage Pattern</summary>
        <pre style={{ backgroundColor: 'var(--pico-card-background-color)', padding: '1rem', borderRadius: 'var(--pico-border-radius)', fontSize: '0.875rem' }}>
{`// In any service file:
import { alertManager } from './alert-system'

class MyService {
  async fetchData() {
    try {
      alertManager.info('Loading data...')
      const data = await api.get('/data')
      alertManager.success('Data loaded successfully!')
      return data
    } catch (error) {
      alertManager.error('Failed to load data')
      throw error
    }
  }
}`}
        </pre>
      </details>
    </section>
  )
}

// Main App Component
// const App: React.FC = () => {
//   return (
//     <AlertProvider>
//       <Demo />
//       <ServiceDemo />
//     </AlertProvider>
//   )
// }

export default AlertProvider